import React from 'react';

/**
 * Error boundary specifico per tabelle/liste che spesso vanno in crash
 * a causa di dati anomali (date malformate, campi mancanti, ecc.).
 *
 * Se un figlio lancia un errore durante il render, mostra un messaggio
 * user-friendly + un pulsante "Riprova" che azzera lo stato di errore.
 *
 * Uso:
 *   <SafeTableBoundary label="fatture">
 *     <Table>...</Table>
 *   </SafeTableBoundary>
 */
export class SafeTableBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[SafeTableBoundary]', error, info);
    // iter90: log al backend per audit crash produzione (best-effort, non blocca)
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      if (url) {
        fetch(`${url}/api/client-errors`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `[SafeTableBoundary${this.props.label ? ':' + this.props.label : ''}] ${String(error?.message || error)}`,
            stack: String(error?.stack || ''),
            component_stack: String(info?.componentStack || ''),
            severity: 'critical',
            location: window.location.href,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {});
      }
    } catch (_) { /* silent */ }
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      return (
        <div className="p-6 bg-red-50 border-2 border-red-200 rounded-lg" data-testid="safe-table-error">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Errore nella visualizzazione {this.props.label ? `di ${this.props.label}` : ''}
          </h3>
          <p className="text-sm text-red-700 mb-3">
            Un dato anomalo sta impedendo la visualizzazione della lista. Il resto della pagina è ancora usabile.
          </p>
          <details className="mb-3">
            <summary className="text-xs text-red-600 cursor-pointer hover:underline">Dettagli tecnici</summary>
            <pre className="text-[10px] font-mono bg-white p-2 rounded border border-red-100 mt-1 whitespace-pre-wrap break-all">{msg}</pre>
          </details>
          <button
            type="button"
            onClick={this.handleReset}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            data-testid="safe-table-retry"
          >
            Riprova
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default SafeTableBoundary;
